import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    console.log('[CartDrawer] connectedCallback - Component mounted');
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // If dialog is already open when component reconnects, re-attach event listeners
    // This can happen when the component is reconnected during morphing
    if (this.refs.dialog?.open) {
      console.log('[CartDrawer] Dialog is already open on connect, re-attaching listeners');
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    console.log('[CartDrawer] disconnectedCallback - Component unmounted');
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Clean up dialog event listeners if dialog is open
    if (this.refs.dialog?.open) {
      console.log('[CartDrawer] Cleaning up event listeners on disconnect');
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('keydown', this.#handleKeyDown);
    }
  }

  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (dialog && this.isClickedOutside(event, dialog)) {
      console.log('[CartDrawer] Click outside detected, closing');
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      console.log('[CartDrawer] Escape key pressed, closing');
      event.preventDefault();
      this.closeDialog();
    }
  };

  /**
   * Check if click is outside the dialog
   */
  isClickedOutside(event, element) {
    if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
      return !this.isPointWithinElement(event.clientX, event.clientY, element);
    }
    return !element.contains(event.target);
  }

  /**
   * Check if a point is within an element
   */
  isPointWithinElement(x, y, element) {
    const rect = element.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  #handleCartAdd = () => {
    console.log('[CartDrawer] CartAddEvent received, auto-open:', this.hasAttribute('auto-open'));
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
